<template>
  <div class="vcl-chart-tooltip" v-if="lines && lines.length" :style="tooltipStyles">
    <div class="tooltip__title">
      <slot name="header" :header="{ ...title, rawTime: item.rawTime }">
        <span class="title__text">{{ title.text }}</span>
        <span v-if="enableDateHeader" class="title__date">{{ title.date }}</span>
      </slot>
    </div>
    <div class="tooltip__content">
      <slot name="content" :headers="headers" :lines="lines">
        <table class="content__table">
          <tr v-if="headers" class="content__header">
            <th></th>
            <th v-for="(header, i) in headers" :key="'content-header-'+i" class="header__item">{{ header }}</th>
          </tr>
          <template v-if="lines">
            <tr v-for="(line, k) in lines" :key="'content-line-'+k" class="content__line" :style="{ backgroundColor: line.rowBackgroundColor }">
              <td class="line__label">
                <div class="label__point" :style="{ backgroundColor: line.backgroundColor }"></div>
                <span class="label__text">
                  {{ line.label }}
                </span>
              </td>
              <td v-for="(value, j) in line.values" :key="'value-'+j" class="line__value" :class="{ 'no-headers': !headers } ">
                {{ value.unit ? `${value.value} ${value.unit}` : value.value }}
              </td>
            </tr>
          </template>
        </table>
      </slot>
    </div>
    <i v-if="enableArrow" :class="position"></i>
  </div>
</template>

<script>
import moment from 'moment';
import { Tooltip } from 'chart.js';

export default {
  name: 'VpChartTooltip',
  props: {
    item: {
      type: Object,
      default() {
        return {};
      },
    },
    options: {
      type: Object,
      default() {
        return {};
      }
    },
    styles: {
      type: Object,
      default() {
        return {};
      }
    },
  },
  data() {
    return {
      position: 'right',
    };
  },
  mounted() {
    let chartWidth;
    let tooltipWidth;
    Tooltip.positioners.cursor = (_, eventPosition = {}) => {
      const { x: xPosition, y: yPosition, chart = {}} = eventPosition;
      const { width: _chartWidth, canvas = {} } = chart;
      const { offsetWidth: _tooltipWidth } = canvas.nextSibling || {};
      if (_chartWidth) {
        chartWidth = _chartWidth;
      }
      if (_tooltipWidth) {
        tooltipWidth = _tooltipWidth;
      }

      const isTooltipOverflow = (chartWidth && tooltipWidth && (chartWidth - (xPosition + tooltipWidth)) <= 0);
      const tooltipPosition = isTooltipOverflow ? -1 * (tooltipWidth + 15) : 15;
      this.position = isTooltipOverflow ? 'left' : 'right';

      return { x: xPosition + tooltipPosition, y: yPosition - 24 };
    };
  },
  computed: {
    title() {
      const { title = null, time = null, date, rawTime } = this.item;

      return {
        text: title || time,
        date: title ? moment(rawTime).format('LLL') : date,
      };
    },
    headers() {
      const { options = {} } = this.item;
      const { headers = [] } = options;

      return headers.length ? headers : null;
    },
    lines() {
      const { lines: _lines = [], options = {} } = this.item;
      const { groupBy = null } = options;

      let lines = _lines;
      if (groupBy) {
        const groups = lines.reduce((result, item) => {
          const groupName = item[groupBy] || 'default';
          return Object.assign({}, result, { [groupName]: (result[groupName] || []).concat(item) });
        }, {});

        lines = Object.entries(groups).reduce((result, [key, group]) => {
          if (key === 'default') {
            return result.concat(...group);
          }

          const main = group.find(({ showLine = false }) => showLine);
          if (!main) return result;

          return result.concat({ ...main, values: group });
        }, []);
      }

      return lines
        .filter(({ showTooltip = true }) => showTooltip)
        .map((line, index) => {
          const { label, value, unit, isArea, pointStyle, color = {}, values: _values = null, rowBackgroundColor } = line;
          const { backgroundColor, borderColor } = color;
          return {
            label,
            values: _values || [{ value, unit }],
            icon: isArea ? null : 'circle',
            isArea,
            pointStyle,
            backgroundColor,
            borderColor,
            rowBackgroundColor: rowBackgroundColor || index % 2 === 0 ? '#F8F8F8' : '#fff',
          };
        });
    },
    tooltipStyles() {
      const { arrow = {}, body = {} } = this.styles;
      const { offset = {} } = arrow;
      return {
        '--arrow-top-offset': offset.top || '40%',
        '--body-font-size': body.fontSize || '14px',
        '--body-font-weight': body.fontWeight || '400',
        '--body-font-color': body.fontColor || '#4F4F4F',
        '--body-square-border': body.squareBorder || 'none',
        '--body-value-font-size': body.valueFontSize || body.fontSize || '14px',
        '--body-value-font-color': body.valueFontColor || body.fontColor || '#4F4F4F',
        '--body-value-font-weight': body.valueFontWeight || body.fontWeight || '400',
      };
    },
    enableDateHeader() {
      const { enableDateHeader = true } = this.options;
      return enableDateHeader;
    },
    enableArrow() {
      const { arrow: { display = false } = {} } = this.styles;
      return display;
    },
  },
};
</script>

<style lang="scss">
  #chartjs-tooltip {
    position: absolute;
    margin: 0;
    padding: 0;
    z-index: 1000;
    min-width: 200px;
    display: flex;
    flex-flow: column nowrap;
    pointer-events: none;
    box-sizing: border-box;
  }
  .vcl-chart-tooltip {
    padding: 0;
    margin: 0;
    background: #FFFFFF;
    border: 1px solid #7F7E7E;
    border-radius: 4px;

    // TITLE
    div.tooltip__title {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      padding: 5px 8px;
      margin: 0;
      border-bottom: 1px solid #DBDBDB;
      column-gap: 0.5em;
      .title__text, .title__date {
        font-style: normal;
        color: #4F4F4F;
        line-height: 1;
        padding: 0;
        margin: 0;
        white-space: nowrap;
      }
      .title__text {
        font-weight: 700;
        font-size: 16px;
      }
      .title__date {
        font-weight: 400;
        font-size: 14px;
      }
    }

    // CONTENT
    div.tooltip__content {
      padding: 0 0 5px;
      margin: 2px 0 0;
      display: block;

      table.content__table {
        display: table;
        border-collapse: collapse;
        border: none;
        margin: 0;
        padding: 0;
        width: 100%;
        tr, th, td {
          border: none;
        }
      }
      // Content Header
      tr.content__header {
        font-weight: bold;
        margin: 0 2px 4px;
        padding: 0 4px;
        text-align: center !important;
        background-color: #fff !important;
        .header__item {
          text-align: center !important;
          font-family: 'Mulish', Helvetica, Arial, sans-serif;
          font-style: normal;
          font-weight: 700;
          font-size: 11px;
          line-height: 1;
          text-transform: uppercase;
          margin: 0;
          padding: 0;
        }
      }

      // Content Lines
      tr.content__line {
        padding: 0;
        margin: 0;

        // label
        td.line__label {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          column-gap: 0.5em;
          margin: 0;
          padding: 0px 7px;

          // Point styles
          div.label__point {
            width: 16px;
            height: 16px;
            border-radius: 2px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0;
            padding: 0;
            border: var(--body-square-border);
          }

          // Text styles
          span.label__text {
            font-family: 'Mulish', Helvetica, Arial, sans-serif;
            font-style: normal;
            font-weight: var(--body-font-weight);
            font-size: var(--body-font-size);
            line-height: 1;
            color: var(--body-font-color);
            white-space: nowrap;
          }
        }
        // values
       td.line__value {
          font-family: 'Mulish', Helvetica, Arial, sans-serif;
          font-style: normal;
          font-weight: var(--body-value-font-weight);
          font-size: var(--body-value-font-size);
          color: var(--body-value-font-color);
          margin: 0;
          padding: 0 4px;
          text-align: center;
          white-space: nowrap;
          &.no-headers {
            text-align: right;
            padding-right: 7px;
          }
        }
      }
    }

    i.right, i.left {
      position: absolute;
      top: var(--arrow-top-offset);
      width: 24px;
      height: 12px;
      overflow: hidden;
      &:after {
        content:'';
        position: absolute;
        width: 12px;
        height: 12px;
        left: 50%;
        transform: translate(-50%,50%) rotate(45deg);
        background-color: #fff;
        border: 1px solid #7F7E7E; box-shadow:0 1px 8px 0px 1px 4px rgba(0, 0, 0, 0.1);
      }
    }
    i.right {
      left: -5px;
      margin-left: -12px;
      transform: rotate(-90deg);
    }
    i.left {
      right: -5px;
      margin-right: -12px;
      transform: rotate(90deg);
    }
  }
</style>
